import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Button from './Button';
import { notificationService } from '../../services/notificationService';

const Header = () => {
  const navigate = useNavigate();
  const { user, userProfile, signOut, loading, sessionChecked } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  // Load unread notification count
  useEffect(() => {
    if (user) {
      loadUnreadCount();

      // Subscribe to notification updates for real-time count
      const role = getUserRole();
      const channel = notificationService?.subscribeToNotifications(
        user?.id,
        role,
        () => {
          // Reload count when notifications change
          loadUnreadCount();
        }
      );

      return () => {
        if (channel) {
          channel?.unsubscribe();
        }
      };
    }
  }, [user]);

  const loadUnreadCount = async () => {
    try {
      const role = getUserRole();
      const count = await notificationService?.getUnreadCount(role);
      setUnreadCount(count);
    } catch (error) {
      console.error('Error loading unread count:', error);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const getUserDisplayName = () => {
    if (userProfile?.full_name) return userProfile?.full_name;
    if (user?.user_metadata?.full_name) return user?.user_metadata?.full_name;
    if (user?.email) return user?.email?.split('@')?.[0];
    return 'User';
  };

  const getUserRole = () => {
    if (userProfile?.role) return userProfile?.role;
    if (user?.user_metadata?.role) return user?.user_metadata?.role;
    return 'customer';
  };

  // Smart home navigation based on auth state and role
  const handleLogoClick = () => {
    // Wait for auth to fully initialize before routing
    if (loading || !sessionChecked) {
      return; // Don't navigate while loading
    }

    if (user) {
      const role = getUserRole();
      
      // Navigate to appropriate dashboard based on role
      switch (role) {
        case 'customer': navigate('/customer-dashboard');
          break;
        case 'agent': navigate('/agent-dashboard');
          break;
        case 'staff': case'admin': navigate('/staff-dashboard');
          break;
        default:
          navigate('/customer-dashboard');
      }
    } else {
      // Not authenticated - go to login
      navigate('/login');
    }
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <button 
              onClick={handleLogoClick}
              className="flex items-center space-x-2"
            >
              <img
                src="/assets/images/No_Background-1763506485544.png"
                alt="HoliYay logo featuring a friendly fox mascot with Scottish heritage"
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold text-gray-900">Holiyay</span>
            </button>
          </div>

          {/* Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {user ? (
              <>
                <button 
                  onClick={() => {
                    const role = getUserRole();
                    if (role === 'agent') {
                      navigate('/agent/messages-v2');
                    } else {
                      navigate('/customer/messages-v2');
                    }
                  }}
                  className="text-gray-600 hover:text-gray-900 font-medium"
                >
                  Messages
                </button>
                
                {/* Notifications Button with Badge */}
                <button
                  onClick={() => {
                    const role = getUserRole();
                    if (role === 'agent') {
                      navigate('/agent-notification-centre');
                    } else {
                      navigate('/customer-notification-centre');
                    }
                  }}
                  className="relative text-gray-600 hover:text-gray-900 font-medium"
                >
                  Notifications
                  {unreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </button>
                
                {getUserRole() === 'customer' && (
                  <>
                    <button 
                      onClick={() => navigate('/create-enquiry')}
                      className="text-gray-600 hover:text-gray-900 font-medium"
                    >
                      Create Enquiry
                    </button>
                    <button 
                      onClick={() => navigate('/customer-dashboard')}
                      className="text-gray-600 hover:text-gray-900 font-medium"
                    >
                      Dashboard
                    </button>
                  </>
                )}
                
                {getUserRole() === 'agent' && (
                  <button 
                    onClick={() => navigate('/agent-dashboard')}
                    className="text-gray-600 hover:text-gray-900 font-medium"
                  >
                    Dashboard
                  </button>
                )}
                
                {getUserRole() === 'staff' && (
                  <button 
                    onClick={() => navigate('/staff-dashboard')}
                    className="text-gray-600 hover:text-gray-900 font-medium"
                  >
                    Dashboard
                  </button>
                )}
              </>
            ) : (
              <>
                <button 
                  onClick={() => navigate('/customer-registration')}
                  className="text-gray-600 hover:text-gray-900 font-medium"
                >
                  Sign Up
                </button>
                <Button
                  onClick={() => navigate('/login')}
                  variant="primary"
                >
                  Log In
                </Button>
              </>
            )}
          </div>

          {/* User Menu - visible on all screen sizes when logged in */}
          {user && (
            <div className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center space-x-2 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                <div className="w-8 h-8 bg-orange-500 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-medium">
                    {getUserDisplayName()?.charAt(0)?.toUpperCase()}
                  </span>
                </div>
                <span className="hidden md:block">{getUserDisplayName()}</span>
                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>

              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-200">
                    <div className="font-medium text-gray-900">{getUserDisplayName()}</div>
                    <div className="capitalize">{getUserRole()}</div>
                  </div>
                  
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      const role = getUserRole();
                      if (role === 'agent') {
                        navigate('/agent-account-settings');
                      } else {
                        navigate('/customer-account-settings');
                      }
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Settings
                  </button>
                  
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      const role = getUserRole();
                      if (role === 'agent') {
                        navigate('/agent-notification-centre');
                      } else {
                        navigate('/customer-notification-centre');
                      }
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 relative"
                  >
                    Notifications
                    {unreadCount > 0 && (
                      <span className="absolute right-4 top-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      const role = getUserRole();
                      if (role === 'agent') {
                        navigate('/agent/messages-v2');
                      } else {
                        navigate('/customer/messages-v2');
                      }
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Messages
                  </button>
                  
                  <hr className="my-1" />
                  
                  <button
                    onClick={() => {
                      setDropdownOpen(false);
                      handleSignOut();
                    }}
                    className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    Log Out
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Mobile menu - shows Log In button when not authenticated */}
          {!user && (
            <div className="md:hidden">
              <Button
                onClick={() => navigate('/login')}
                variant="primary"
                size="sm"
              >
                Log In
              </Button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;